import { SITE_CONFIG, FOOTER_LINKS } from '@/lib/constants'
import type { ContactContent, FaqItem, OrganizationContent, ServiceItem } from '@/api/types'

type JsonLd = Record<string, unknown>

const DEFAULT_LOGO = `${SITE_CONFIG.url}/images/SVGs/logo.svg`

const DEFAULT_SAME_AS = [
  ...FOOTER_LINKS.social.map((s) => s.url).filter((url) => url && url !== 'https://facebook.com'),
]

const DEFAULT_FAQS: FaqItem[] = [
  {
    id: 1,
    question: 'What digital transformation services does DIGITREK offer in Dubai?',
    answer:
      'DIGITREK offers web development, mobile app development, AI solutions, Odoo ERP implementation, UI/UX design, custom software development, and data analytics — all based in Dubai, UAE.',
    category: '',
    order: 1,
  },
  {
    id: 2,
    question: 'Where is DIGITREK located?',
    answer:
      'DIGITREK is based in Dubai, United Arab Emirates, and serves clients across Dubai, the UAE, and the wider Middle East region.',
    category: '',
    order: 2,
  },
  {
    id: 3,
    question: 'Does DIGITREK implement Odoo ERP for businesses in the UAE?',
    answer:
      'Yes. DIGITREK provides end-to-end Odoo ERP implementation, customization, and support for businesses in Dubai and across the UAE.',
    category: '',
    order: 3,
  },
  {
    id: 4,
    question: 'How can I contact DIGITREK for a free audit?',
    answer: `You can reach DIGITREK by emailing ${SITE_CONFIG.email}, calling ${SITE_CONFIG.phone}, or filling out the contact form on our website at ${SITE_CONFIG.url}.`,
    category: '',
    order: 4,
  },
]

function pickText(...values: (string | undefined | null)[]): string {
  for (const value of values) {
    const text = typeof value === 'string' ? value.trim() : ''
    if (text) return text
  }
  return ''
}

export function buildOrganizationJsonLd(
  organization: OrganizationContent | null,
  contact?: ContactContent
): JsonLd {
  const name = pickText(organization?.name, SITE_CONFIG.name)
  const description = pickText(
    organization?.description,
    SITE_CONFIG.description
  )
  const url = pickText(organization?.url, SITE_CONFIG.url)
  const email = pickText(organization?.email, contact?.email, SITE_CONFIG.email)
  const telephone = pickText(organization?.telephone, contact?.phone, SITE_CONFIG.phone)
  const logo = pickText(organization?.logo, DEFAULT_LOGO)
  const addressText = pickText(organization?.address, contact?.address, SITE_CONFIG.address)
  const latitude = organization?.latitude ?? 25.2048
  const longitude = organization?.longitude ?? 55.2708
  const areaServed = pickText(organization?.areaServed, 'United Arab Emirates')
  const sameAs = organization?.sameAs?.length ? organization.sameAs : DEFAULT_SAME_AS

  const jsonLd: JsonLd = {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'LocalBusiness'],
    name,
    url,
    logo,
    description,
    address: {
      '@type': 'PostalAddress',
      streetAddress: addressText,
      addressLocality: 'Dubai',
      addressCountry: 'AE',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude,
      longitude,
    },
    email,
    areaServed: areaServed.includes(',')
      ? areaServed.split(',').map((part) => part.trim()).filter(Boolean)
      : [areaServed, 'UAE', 'Middle East'],
    serviceType: [
      'Web Development',
      'Mobile App Development',
      'AI Solutions',
      'Odoo ERP',
      'UI/UX Design',
      'Custom Software Development',
    ],
    sameAs,
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
  }

  if (organization?.legalName) jsonLd.legalName = organization.legalName
  if (organization?.foundingDate) jsonLd.foundingDate = organization.foundingDate
  if (organization?.priceRange) jsonLd.priceRange = organization.priceRange
  if (telephone) jsonLd.telephone = telephone

  return jsonLd
}

export function buildWebsiteJsonLd(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_CONFIG.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }
}

export function buildFaqJsonLd(faqs: FaqItem[]): JsonLd | null {
  const items = (faqs.length ? faqs : DEFAULT_FAQS).filter(
    (item) => item.question.trim() && item.answer.trim()
  )
  if (!items.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}

export function buildServicesCatalogJsonLd(services: ServiceItem[]): JsonLd | null {
  const items = services.filter((item) => item.title.trim())
  if (!items.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Digital Transformation Services',
    provider: {
      '@type': 'Organization',
      name: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
    },
    areaServed: {
      '@type': 'Country',
      name: 'United Arab Emirates',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Digital Services',
      itemListElement: items.map((item) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: item.title,
          description: item.description,
        },
      })),
    },
  }
}

export function buildHomeJsonLd(options: {
  organization: OrganizationContent | null
  faqs: FaqItem[]
  contact?: ContactContent
  services?: ServiceItem[]
}): JsonLd[] {
  const blocks: JsonLd[] = [
    buildOrganizationJsonLd(options.organization, options.contact),
    buildWebsiteJsonLd(),
  ]

  const faqBlock = buildFaqJsonLd(options.faqs)
  if (faqBlock) blocks.push(faqBlock)

  const servicesBlock = buildServicesCatalogJsonLd(options.services ?? [])
  if (servicesBlock) blocks.push(servicesBlock)

  return blocks
}
